import { IconMinus, IconPlus } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Text } from 'app/design/primitives/text';
import type { UseFormReturn } from 'react-hook-form';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH, MAX_LISTING_DUPLICATES } from 'app/utils/constants';
import { useUISize } from 'app/utils/web-helpers';

const theme = createDSTheme();

export function DuplicatesStepper({ form, name = 'duplicates' }: { form: UseFormReturn<any>; name?: string }) {
  const { t } = useTranslation('new');
  const { isSmallUI } = useUISize();
  const value = form.watch(name) as number | null | undefined;
  const numericValue = typeof value === 'number' ? value : 0;

  const setValue = (next: number) => {
    const clamped = Math.max(0, Math.min(MAX_LISTING_DUPLICATES, Math.floor(next)));
    form.setValue(name, clamped || null, { shouldDirty: true, shouldValidate: true });
  };

  const decrement = () => setValue(numericValue - 1);
  const increment = () => setValue(numericValue + 1);

  const label = (
    <Column flex={isSmallUI ? undefined : 1} gap="spacing.general.x1">
      <Text fontWeight="700" variant="bodyBase">
        {t('duplicatesStepper.title')}
      </Text>
      <Text color="text.secondary" variant="bodySmall">
        {t('duplicatesStepper.text')}
      </Text>
    </Column>
  );

  const stepper = (
    <Row
      alignItems="center"
      backgroundColor="surface.default"
      borderRadius="radius.lg"
      gap="spacing.general.x2"
      justifyContent="space-between"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
      width={isSmallUI ? '100%' : undefined}
    >
      <Pressable
        accessibilityLabel={t('duplicatesStepper.decrement')}
        disabled={numericValue <= 0}
        hitSlop={8}
        onPress={decrement}
        style={{ opacity: numericValue <= 0 ? 0.4 : 1, padding: 4 }}
        testID="duplicates-stepper-decrement"
      >
        <IconMinus color={theme.colors['text.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>

      <View
        style={
          isSmallUI
            ? { flex: 1, minWidth: 80 }
            : { minWidth: 40, maxWidth: Number(theme.tokens.sizing['sizing.general.x40']) }
        }
      >
        <ControlledInput
          {...{ control: form.control }}
          keyboardType="number-pad"
          maxLength={2}
          name={name}
          nullable
          placeholder="0"
          style={{ minHeight: isSmallUI ? 48 : undefined }}
          textAlign="center"
          testID="duplicates-stepper-input"
        />
      </View>

      <Pressable
        accessibilityLabel={t('duplicatesStepper.increment')}
        disabled={numericValue >= MAX_LISTING_DUPLICATES}
        hitSlop={8}
        onPress={increment}
        style={{ opacity: numericValue >= MAX_LISTING_DUPLICATES ? 0.4 : 1, padding: 4 }}
        testID="duplicates-stepper-increment"
      >
        <IconPlus color={theme.colors['text.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    </Row>
  );

  return (
    <Column
      backgroundColor="surface.tertiary-neutral"
      borderRadius="radius.xl"
      gap="spacing.general.x2"
      padding="spacing.general.x4"
    >
      {isSmallUI ? (
        <Column gap="spacing.general.x3">
          {label}
          {stepper}
        </Column>
      ) : (
        <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between">
          {label}
          {stepper}
        </Row>
      )}
    </Column>
  );
}
